import ClassNames from 'classnames'
import PropTypes from 'prop-types'
import React from 'react'
import Select, { components as RSComponents, createFilter } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { default as TYPOGRAPHYS } from './../../../themes/styles/bases/typographys'
import { default as VARIABLES } from './../../../themes/styles/bases/variables'
import { SelectControlWrapper } from './styled'

const ACTION_VALUE = 'ACTION_OPTION';

const buildActionOption = (label) => ({
  value: ACTION_VALUE,
  label,
  __isAction: true,
});

// Option พิเศษ: ถ้าเป็น action ให้เรียก fn และไม่ให้ select ค่า
const ActionAwareOption = (onTopActionClick) => (props) => {
  const { data, innerProps } = props;
  const isAction = data && data.__isAction;

  if (!isAction) return <RSComponents.Option {...props} />;

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onTopActionClick === 'function') onTopActionClick();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (typeof onTopActionClick === 'function') onTopActionClick();
    }
  };

  return (
    <div
      {...innerProps}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      className="react-select__option react-select__option--is-action"
      style={{ cursor: 'pointer' }}
    >
      {data.label}
    </div>
  );
};

export class SelectControl extends React.PureComponent {
  render() {
    const {
      className,
      label,
      labelFontStyle,
      labelWidth,
      labelSpacingTop,
      labelSpacingRight,
      labelFontColor,
      labelAlign,
      error,
      errorColor,
      success,
      successColor,
      disabled,
      disabledColor,
      message,
      messageFontStyle,
      messageSpacingTop,
      messageSpacingLeft,
      messageFontColor,
      messageAlign,
      noArrow,
      width,
      stackLabel,
      verticalLabel,
      required,
      filter,
      filterLarge,
      filterLargePlaceholderShort,
      filterLargePlaceholderLong,
      filterFluid,
      filterBorder,
      orderInquirySubmitted,
      orderInquiryAcknownledged,
      idPhone,
      idPhoneGlobal,
      isCreatable,
      isFilterSearch,
      isFilterSearchBorder,
      optionHeight,

      topActionLabel,       // string | react node ข้อความบนสุด เช่น "+ เพิ่มรายการ..."
      onTopActionClick,     // function ที่จะเรียกเมื่อกด option บนสุด
      options = [],
      onChange,
      filterOption,         // เผื่อผู้ใช้มี filterOption เอง
      components,           // เผื่อผู้ใช้ส่ง component override เอง
      ...restProps
    } = this.props

    // props for css classes
    const classes = ClassNames(
      'select-control',
      { 'is-error': error },
      { 'is-success': success },
      { 'is-disabled': disabled },
      { 'is-no-arrow': noArrow },
      { 'is-stack-label': stackLabel },
      { 'is-vertical-label': verticalLabel },
      { 'is-required': required },
      { 'is-filter': filter },
      { 'is-filter-large': filterLarge },
      { 'is-filter-large-placeholder-short': filterLargePlaceholderShort },
      { 'is-filter-large-placeholder-long': filterLargePlaceholderLong },
      { 'is-filter-fluid': filterFluid },
      { 'is-filter-border': filterBorder },
      { 'is-order-inquiry-submitted': orderInquirySubmitted },
      { 'is-order-inquiry-acknownledged': orderInquiryAcknownledged },
      { 'is-id-phone': idPhone },
      { 'is-id-phone-global': idPhoneGlobal },
      { 'is-filter-search': isFilterSearch },
      { 'is-filter-search-border': isFilterSearchBorder },
      className
    )

    // รวม options พร้อม option action ไว้บนสุด (ถ้ามี onTopActionClick)
    const mergedOptions = onTopActionClick && topActionLabel
      ? [buildActionOption(topActionLabel), ...options]
      : options;

    // กรอง: ให้ action-option แสดงเสมอ (แม้พิมพ์ค้นหา) แล้วค่อยใช้ default filter ต่อ
    const defaultFilter = createFilter({ ignoreAccents: true, trim: true });
    const safeFilterOption = (candidate, input) => {
      if (candidate?.data?.__isAction) return true;
      return filterOption ? filterOption(candidate, input) : defaultFilter(candidate, input);
    };

    // กันการเปลี่ยนค่าเวลาเลือก action-option
    const handleChange = (value, meta) => {
      // single/multi: เช็คตอน select-option
      if (meta?.action === 'select-option' && meta?.option?.__isAction) {
        if (typeof onTopActionClick === 'function') onTopActionClick();
        return; // ไม่ส่งต่อ onChange เพื่อไม่ให้ค่าเปลี่ยน
      }
      if (typeof onChange === 'function') onChange(value, meta);
    };

    // override components เฉพาะ Option (ถ้ามี action)
    const mergedComponents = onTopActionClick
      ? {
        ...components,
        Option: ActionAwareOption(onTopActionClick),
      }
      : components;

    const SelectImpl = isCreatable ? CreatableSelect : Select;

    return (
      <SelectControlWrapper
        className={classes}
        theme={{
          labelFontStyle,
          labelWidth,
          labelSpacingTop,
          labelSpacingRight,
          labelFontColor,
          labelAlign,
          errorColor,
          successColor,
          disabledColor,
          messageFontStyle,
          messageSpacingTop,
          messageSpacingLeft,
          messageFontColor,
          messageAlign,
          width,
          optionHeight
        }}
      >
        {label &&
          <div className='select-control-label'>
            {label}
          </div>
        }
        <div className='select-control-select'>
          <SelectImpl
            {...restProps}
            options={mergedOptions}
            onChange={handleChange}
            components={mergedComponents}
            filterOption={safeFilterOption}
            {...(isCreatable && {
              formatCreateLabel: (optionText) => `Add "${optionText}" to the list`,
            })}
          />
          {message &&
            <div className='select-control-message'>
              {message}
            </div>
          }
        </div>
      </SelectControlWrapper>
    )
  }

  static defaultProps = {
    labelFontStyle: TYPOGRAPHYS.FIRST.REGULAR.FS_14,
    labelFontColor: VARIABLES.COLORS.GRAY_19,
    errorColor: VARIABLES.COLORS.ERROR,
    successColor: VARIABLES.COLORS.SUCCESS,
    disabledColor: VARIABLES.COLORS.DISABLED,
    className: 'react-select',
    classNamePrefix: 'react-select',
    theme: {
      colors: {
        primary: 'transparent',  // bg color option selected
        primary25: 'transparent' // bg color option hover
      }
    },
    isSearchable: false,
  }

  static propTypes = {
    className: PropTypes.string
  }
}
